import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { OperationalShell } from "../components/OperationalShell";
import { ModuleRow } from "../components/ModuleRow";
import { LockDialog, ScheduleDialog, BreakGlassDialog } from "../components/GovernanceDialogs";
import { PropagationSummaryCard } from "../modules/activityPulse/PropagationSummaryCard";
import { useCapability } from "../useCapability";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Navigate } from "react-router-dom";
import { HierarchyPanel } from "../components/HierarchyPanel";
import { GuidedArrivalBanner } from "../components/GuidedArrivalBanner";
import { useGuidedActionParams } from "../guidedAction";
import { GuidanceTooltip } from "../guidance/GuidanceTooltip";

/**
 * Module Management - Super Admin provisioning surface (Phase 3).
 *
 * Pure capability-driven governance UX. The page asks the backend for a
 * full per-module resolution and renders one row per module. Toggling a
 * row issues a PUT - the backend re-resolves and the row updates with
 * fresh provenance.
 *
 * Phase 3 directive: as governance grows in power, UX gets simpler.
 *   * Plain-language module descriptions (trilingual)
 *   * Plain-language provenance badges
 *   * Plain-language impact preview dialog ("Disabling Vouchers will
 *     remove: voucher tile, voucher navigation, voucher analytics …")
 *   * Lock state surfaces only as a calm badge - no JSON, no raw fields
 *
 * No bypass paths. Even Super Admins flow through `requires_module` on
 * the backend. The shell itself relies on `useCapability()`.
 */
const ModuleManagement = () => {
    const { t, i18n } = useTranslation();
    const { user, loading: authLoading } = useAuth();
    const { map, refresh: refreshCapability } = useCapability();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [impactFor, setImpactFor] = useState(null);
    const [lockFor, setLockFor] = useState(null);
    const [scheduleFor, setScheduleFor] = useState(null);
    const [breakGlassFor, setBreakGlassFor] = useState(null);
    const [propagationToast, setPropagationToast] = useState(null);
    const [activeOrgId, setActiveOrgId] = useState(null);
    const [highlightedModule, setHighlightedModule] = useState(null);
    // v4.0.0 — Back-to-Base anchor: lightweight reassurance that
    // appears AFTER a trace-origin context jump. Stores both the prior
    // org + the prior scroll position so a single tap restores the
    // operator's exact previous view. Fades naturally after 12s.
    const [backAnchor, setBackAnchor] = useState(null);

    // v4.2.1 — Guided Action arrival. If we landed via ?guided_from=...
    // pre-select the org and pre-focus the module from query string.
    const guidedArrival = useGuidedActionParams();
    const [guidedBanner, setGuidedBanner] = useState(null);
    useEffect(() => {
        if (!guidedArrival.isGuided) return;
        const { org, focus_module } = guidedArrival.params;
        if (org) setActiveOrgId(org);
        if (focus_module) {
            setHighlightedModule(focus_module);
            // Scroll/pulse the row once it renders.
            setTimeout(() => {
                const el = document.querySelector(
                    `[data-testid="ob-module-row-${focus_module}"]`,
                );
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 600);
            // Fade the highlight after 4s.
            setTimeout(() => setHighlightedModule(null), 4000);
        }
        // Build a calm arrival banner. The explanation comes from the
        // briefing — but we only have the kind + params in the URL, so
        // we use i18n keys keyed on `kind`.
        setGuidedBanner({
            kind: guidedArrival.kind || "review_module",
            org,
            focus_module,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const orgId = user?.organization_id;
    const isSuperAdmin = user?.role === "super_admin";

    const lang = (i18n.language || "en").split("-")[0];

    /**
     * v3.8.1 — Trace Origin handler.
     *
     * From any descendant row, jump to the originating ancestor and
     * briefly highlight the module that owns the policy. No modal,
     * no graph, no breadcrumb — just a calm context shift.
     */
    const handleTraceOrigin = (sourceOrgId, moduleName) => {
        if (!sourceOrgId || !moduleName) return;
        // v4.0.0 — capture the prior context BEFORE we navigate away,
        // so the Back-to-Base anchor can restore it perfectly.
        const priorOrgId = data?.organization?.id || activeOrgId || orgId;
        const priorOrgName = data?.organization?.name;
        const priorScrollY = typeof window !== "undefined" ? window.scrollY : 0;
        if (priorOrgId && priorOrgId !== sourceOrgId) {
            setBackAnchor({
                orgId: priorOrgId,
                orgName: priorOrgName,
                scrollY: priorScrollY,
                expiresAt: Date.now() + 12000,
            });
            // Auto-fade the anchor after 12s.
            setTimeout(() => {
                setBackAnchor((cur) => (cur && cur.orgId === priorOrgId ? null : cur));
            }, 12000);
        }
        setActiveOrgId(sourceOrgId);
        setHighlightedModule(moduleName);
        const tryScroll = (attempt = 0) => {
            const el = document.querySelector(`[data-testid="ob-module-row-${moduleName}"]`);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                return;
            }
            if (attempt < 4) setTimeout(() => tryScroll(attempt + 1), 350);
        };
        setTimeout(() => tryScroll(0), 250);
        setTimeout(() => setHighlightedModule(null), 2600);
    };

    /**
     * v4.0.0 — Back-to-Base. Restore both the previously inspected org
     * AND the operator's prior scroll position. Single tap, single
     * smooth animation, no chrome added to the page.
     */
    const handleBackToBase = () => {
        if (!backAnchor) return;
        const target = backAnchor;
        setBackAnchor(null);
        setActiveOrgId(target.orgId);
        setTimeout(() => {
            window.scrollTo({ top: target.scrollY || 0, behavior: "smooth" });
        }, 350);
    };

    const fetchModules = async (oid) => {
        setLoading(true);
        try {
            const res = await api.get(`/operational-base/organizations/${oid}/modules`);
            setData(res.data);
        } catch (err) {
            const msg = err?.response?.data?.detail || t("ob.errors.load_modules");
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const resolveOrg = async () => {
            // Honor the user-selected hierarchy node first.
            if (activeOrgId) return activeOrgId;
            if (orgId) return orgId;
            if (!isSuperAdmin) return null;
            try {
                // Prefer the seeded St-Hubert corporate root if present.
                const res = await api.get("/operational-base/hierarchy");
                const tree = res.data?.hierarchy || [];
                const stHubert = tree.find((n) => n.name === "St-Hubert Corporate");
                if (stHubert) return stHubert.id;
                if (tree[0]) return tree[0].id;
            } catch (_) {
                // fall through
            }
            try {
                const res = await api.get("/organizations");
                const list = res.data?.organizations || res.data || [];
                return list[0]?.id || null;
            } catch (_) {
                return null;
            }
        };
        let cancelled = false;
        (async () => {
            const oid = await resolveOrg();
            if (cancelled) return;
            if (!oid) {
                setLoading(false);
                return;
            }
            await fetchModules(oid);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, isSuperAdmin, activeOrgId]);

    const handleToggle = async (moduleName, enabled) => {
        if (!dataOrgId) return;
        setBusy(true);
        try {
            await api.put(
                `/operational-base/organizations/${data.organization.id}/modules/${moduleName}`,
                { enabled },
            );
            toast.success(t(enabled ? "ob.modules.enabled_toast" : "ob.modules.disabled_toast"));
            await fetchModules(data.organization.id);
            // The user's own capability map may have changed - refresh it so
            // the shell reshapes immediately. ("Invisible means non-existent"
            // becomes a real, demonstrable behavior).
            refreshCapability && refreshCapability();
        } catch (err) {
            const msg = err?.response?.data?.detail || t("ob.errors.toggle_failed");
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const groups = useMemo(() => {
        if (!data?.modules) return { operational: [], governance: [], admin: [], meta: [] };
        const out = { operational: [], governance: [], admin: [], analytics: [], meta: [] };
        for (const m of data.modules) {
            const cat = m.governance_category || "operational";
            if (!out[cat]) out[cat] = [];
            out[cat].push(m);
        }
        return out;
    }, [data]);

    if (authLoading || !user) {
        return <OperationalShell />;
    }
    const dataOrgId = data?.organization?.id || null;
    const dataOrgName = data?.organization?.name || null;

    if (!isSuperAdmin) {
        return <Navigate to="/operational-base" replace />;
    }

    return (
        <OperationalShell>
            <header className="mb-10 lg:mb-14 space-y-3" data-testid="ob-modules-header">
                <p className="ob-eyebrow">{t("ob.modules.eyebrow")}</p>
                <h1
                    className="text-4xl sm:text-5xl flex items-center"
                    style={{ color: "var(--ob-text-primary)" }}
                    data-testid="ob-modules-title"
                >
                    {dataOrgName
                        ? t("ob.modules.title_named", { name: dataOrgName })
                        : t("ob.modules.title")}
                    <GuidanceTooltip guidanceKey="modules.intro" />
                </h1>
                <p
                    className="text-base max-w-2xl"
                    style={{ color: "var(--ob-text-secondary)" }}
                >
                    {t("ob.modules.subtitle", { name: dataOrgName || "" })}
                </p>
            </header>

            {guidedBanner && (
                <GuidedArrivalBanner
                    title={t("ob.guided.from_analytics")}
                    explanation={
                        guidedBanner.focus_module
                            ? t("ob.modules.guided_review_module_explanation", {
                                  name: dataOrgName || "",
                              })
                            : t("ob.modules.guided_review_org_explanation", {
                                  name: dataOrgName || "",
                              })
                    }
                    evidence={null}
                    onDismiss={() => setGuidedBanner(null)}
                    testId="ob-modules-guided-banner"
                />
            )}

            {loading && (
                <p className="text-sm" style={{ color: "var(--ob-text-tertiary)" }}>
                    {t("common.loading", { defaultValue: "Loading…" })}
                </p>
            )}

            {!loading && !data && (
                <p
                    data-testid="ob-modules-empty"
                    className="ob-card p-8 text-center"
                    style={{ color: "var(--ob-text-secondary)" }}
                >
                    {t("ob.modules.no_org")}
                </p>
            )}

            {!loading && data && (
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                    {/* Hierarchy panel - Super Admin scope visualizer */}
                    <aside className="lg:w-80 lg:flex-shrink-0 lg:sticky lg:top-20 lg:self-start" data-testid="ob-modules-hierarchy-aside">
                        <HierarchyPanel
                            selectedOrgId={dataOrgId}
                            onSelectOrg={(node) => setActiveOrgId(node.id)}
                        />
                    </aside>

                    <div className="space-y-10 flex-1 min-w-0">
                    {Object.entries(groups).map(([category, modules]) => {
                        if (!modules || modules.length === 0) return null;
                        return (
                            <section
                                key={category}
                                data-testid={`ob-modules-group-${category}`}
                                className="space-y-4"
                            >
                                <h2 className="ob-eyebrow">{t(`ob.modules.group.${category}`, category)}</h2>
                                <div className="space-y-3">
                                    {modules.map((m) => (
                                        <ModuleRow
                                            key={m.module_name}
                                            module={m}
                                            lang={lang}
                                            currentOrgId={dataOrgId}
                                            highlight={highlightedModule === m.module_name}
                                            onToggle={handleToggle}
                                            onShowImpact={setImpactFor}
                                            onLock={setLockFor}
                                            onSchedule={setScheduleFor}
                                            onBreakGlass={setBreakGlassFor}
                                            onTraceOrigin={handleTraceOrigin}
                                            busy={busy}
                                        />
                                    ))}
                                </div>
                            </section>
                        );
                    })}
                    </div>
                </div>
            )}

            {impactFor && (
                <ImpactDialog
                    module={impactFor}
                    lang={lang}
                    onClose={() => setImpactFor(null)}
                />
            )}

            {lockFor && (
                <LockDialog
                    orgId={dataOrgId}
                    module={lockFor.module_name}
                    displayName={lockFor.display_name?.[lang] || lockFor.display_name?.en || lockFor.module_name}
                    onClose={() => setLockFor(null)}
                    onSuccess={(payload) => {
                        setPropagationToast({
                            module: lockFor.module_name,
                            displayName: lockFor.display_name?.[lang] || lockFor.display_name?.en || lockFor.module_name,
                            summary: payload?.propagation,
                        });
                        if (dataOrgId) fetchModules(data.organization.id);
                        refreshCapability && refreshCapability();
                    }}
                />
            )}
            {scheduleFor && (
                <ScheduleDialog
                    orgId={dataOrgId}
                    module={scheduleFor.module_name}
                    displayName={scheduleFor.display_name?.[lang] || scheduleFor.display_name?.en || scheduleFor.module_name}
                    onClose={() => setScheduleFor(null)}
                    onSuccess={() => {
                        if (dataOrgId) fetchModules(data.organization.id);
                    }}
                />
            )}
            {breakGlassFor && (
                <BreakGlassDialog
                    orgId={dataOrgId}
                    module={breakGlassFor.module_name}
                    displayName={breakGlassFor.display_name?.[lang] || breakGlassFor.display_name?.en || breakGlassFor.module_name}
                    onClose={() => setBreakGlassFor(null)}
                    onSuccess={() => {
                        if (dataOrgId) fetchModules(data.organization.id);
                        refreshCapability && refreshCapability();
                    }}
                />
            )}

            {propagationToast && (
                <div className="fixed bottom-6 right-6 z-40 max-w-md" data-testid="ob-propagation-toast">
                    <PropagationSummaryCard
                        summary={propagationToast.summary}
                        displayName={propagationToast.displayName}
                        onDismiss={() => setPropagationToast(null)}
                    />
                </div>
            )}

            {backAnchor && (
                <button
                    type="button"
                    onClick={handleBackToBase}
                    data-testid="ob-back-to-base-anchor"
                    className="fixed top-20 right-6 z-30 inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 backdrop-blur transition"
                    style={{
                        color: "#A7F3D0",
                        background: "rgba(16,185,129,0.10)",
                        border: "1px solid rgba(16,185,129,0.25)",
                        animation: "obFadeInDown 280ms ease-out",
                    }}
                    title={t("ob.back_to_base.tooltip", { name: backAnchor.orgName })}
                >
                    <span aria-hidden="true">←</span>
                    {t("ob.back_to_base.label", { name: backAnchor.orgName })}
                </button>
            )}
        </OperationalShell>
    );
};

const ImpactDialog = ({ module, lang = "en", onClose }) => {
    const { t } = useTranslation();
    const displayName = module.display_name?.[lang] || module.display_name?.en || module.module_name;

    // Plain-language enumeration of what disappears when the module is
    // disabled. Honors the directive: "Disabling Vouchers will remove:
    // Voucher tile, Voucher navigation, Voucher analytics, …"
    const removals = [
        t("ob.modules.impact.tile", { name: displayName }),
        t("ob.modules.impact.navigation", { name: displayName }),
        t("ob.modules.impact.routes", { name: displayName }),
        t("ob.modules.impact.activity", { name: displayName }),
    ];

    return (
        <div
            role="dialog"
            aria-modal="true"
            data-testid="ob-modules-impact-dialog"
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.6)" }}
            onClick={onClose}
        >
            <div
                role="document"
                onClick={(e) => e.stopPropagation()}
                className="ob-card max-w-md w-full p-6 sm:p-8 space-y-5"
                style={{ background: "var(--ob-surface-elevated)" }}
            >
                <div>
                    <p className="ob-eyebrow">{t("ob.modules.impact.eyebrow")}</p>
                    <h2
                        className="text-2xl mt-1"
                        style={{ color: "var(--ob-text-primary)" }}
                    >
                        {displayName}
                    </h2>
                </div>
                <p style={{ color: "var(--ob-text-secondary)" }}>
                    {t("ob.modules.impact.description", { name: displayName })}
                </p>
                <ul className="space-y-2 list-none">
                    {removals.map((line) => (
                        <li
                            key={line}
                            className="flex items-start gap-3"
                            style={{ color: "var(--ob-text-secondary)" }}
                        >
                            <span className="ob-pulse-dot info mt-2" aria-hidden="true" />
                            <span className="text-sm">{line}</span>
                        </li>
                    ))}
                </ul>
                {module.dependencies?.length > 0 && (
                    <p className="text-xs" style={{ color: "var(--ob-text-tertiary)" }}>
                        {t("ob.modules.impact.dependencies", { deps: module.dependencies.join(", ") })}
                    </p>
                )}
                <div className="flex justify-end pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="ob-emerald-btn"
                        data-testid="ob-modules-impact-close"
                    >
                        {t("common.close", { defaultValue: "Got it" })}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ModuleManagement;
